import type { HealthState } from '../shared/types'

export const stateCopy: Record<
  HealthState,
  { label: string; short: string; description: string }
> = {
  operational: {
    label: 'All systems operational',
    short: 'Operational',
    description: 'Every required check is responding normally.',
  },
  degraded: {
    label: 'Some systems are degraded',
    short: 'Degraded',
    description: 'The fleet is available, but one or more systems need attention.',
  },
  outage: {
    label: 'Service interruption detected',
    short: 'Outage',
    description: 'At least one required system is not operational.',
  },
  maintenance: {
    label: 'Maintenance in progress',
    short: 'Maintenance',
    description: 'One or more systems are intentionally under maintenance.',
  },
  unknown: {
    label: 'System state is incomplete',
    short: 'Unknown',
    description: 'One or more checks have not reported a reliable state.',
  },
}

export function relativeTime(value: string, now = Date.now()) {
  const seconds = Math.round((Date.parse(value) - now) / 1000)
  const formatter = new Intl.RelativeTimeFormat('en', { numeric: 'auto' })
  if (Math.abs(seconds) < 60) return formatter.format(seconds, 'second')
  const minutes = Math.round(seconds / 60)
  if (Math.abs(minutes) < 60) return formatter.format(minutes, 'minute')
  const hours = Math.round(minutes / 60)
  if (Math.abs(hours) < 24) return formatter.format(hours, 'hour')
  return formatter.format(Math.round(hours / 24), 'day')
}

export function clockTime(value: string) {
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
  }).format(new Date(value))
}

export function fullDate(value: string) {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
  }).format(new Date(value))
}

export function shortDate(value: string) {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(value))
}

export function compactLatency(value?: number | null) {
  if (value === undefined || value === null) return '—'
  if (value >= 1000) return `${(value / 1000).toFixed(1)}s`
  return `${Math.round(value)}ms`
}

export function availability(value?: number | null) {
  if (value === undefined || value === null) return 'Collecting'
  return `${value.toFixed(value === 100 ? 1 : 2)}%`
}
